import {
    ApexClient,
    ClientXeroContactData,
    getCampaignManagerClient,
    getCampaignManagerClientXeroContact,
} from '@round/api';
import { useCallback, useState } from 'react';
import { showNotification } from '../../../../utils/showNotification';

type Props = {
    clientId: number | null;
};

export function useCampaignManagerClientInvoiceData({ clientId }: Props) {
    const [client, setClient] = useState<ApexClient | null>(null);
    const [xeroContact, setXeroContact] = useState<ClientXeroContactData | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const init = useCallback(
        async (requestInit: RequestInit) => {
            if (!clientId) {
                return;
            }

            const fetchClient = async (requestInit: RequestInit) => {
                try {
                    const response = await getCampaignManagerClient(clientId, requestInit);

                    if (response.status === 200) {
                        setClient(response.data);
                        return;
                    }

                    if (response.status === 404) {
                        showNotification(`Couldn't find client`, 'error');
                        return;
                    }
                } catch (e) {
                    if (e instanceof Error && e.name === 'AbortError') {
                        return;
                    }

                    showNotification(`Couldn't get client`, 'error');
                }
            };

            const fetchClientXeroContact = async (requestInit: RequestInit) => {
                try {
                    const response = await getCampaignManagerClientXeroContact(clientId, requestInit);

                    if (response.status === 200) {
                        setXeroContact(response.data);
                        return;
                    }

                    if (response.status === 404) {
                        showNotification(`Couldn't find client xero contact`, 'error');
                        return;
                    }

                    if (response.status === 400) {
                        const errorMessage = Array.isArray(response.data) ? response.data.join(', ') : response.data;
                        showNotification(errorMessage, 'error');
                        return;
                    }
                } catch (e) {
                    if (e instanceof Error && e.name === 'AbortError') {
                        return;
                    }
                    showNotification(`Couldn't get client xero contact`, 'error');
                }
            };

            await Promise.all([fetchClient(requestInit), fetchClientXeroContact(requestInit)]);
            setIsInitialized(true);
        },
        [clientId]
    );

    const reset = () => {
        setClient(null);
        setIsInitialized(false);
    };

    return {
        client,
        xeroContact,
        isInitialized,
        init,
        reset,
    };
}
